import React, { useCallback, useMemo } from "react";
import { S3_BASE_URL } from "src/constants/endpoints";
import CheckBox from "src/assests/images/dashboard/Checkbox 2.png";
import RestuarantImg from "src/assests/images/dashboard/default-thumbnail (1).jpg";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { MenuItemOption } from "src/interfaces/menu.interface";
import { MenuItemCardProps } from "src/interfaces/menu-items-props.interface";
import TranslationCompletenessBadge from "src/components/common/TranslationCompletenessBadge";
import { useStorePosLocales, PlatformLocale } from "src/hooks/useStorePosLocales";
import {
  buildLocalizedNameTooltip,
  resolveLocalizedDisplayName,
} from "src/utils/localeTranslations";

const MenuItemCard = React.memo(
  ({
    menuItem,
    handleMenuItemClick,
    isSelected,
    isCombo,
    handleClick,
    menuItemIndexes,
    onClick,
  }: MenuItemCardProps) => {
    const { enabledLocales, defaultLocale, enabledLocaleOptions } = useStorePosLocales();
    const handleCardClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (handleClick) handleClick(menuItemIndexes);
        if (onClick) onClick(e, menuItem);
      },
      [handleClick, menuItemIndexes, onClick, menuItem]
    );

    const handleEditClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick?.(e as unknown as React.MouseEvent<HTMLElement>, menuItem);
      },
      [onClick, menuItem]
    );

    const handleDeleteClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        handleMenuItemClick(menuItem?.correlationId);
      },
      [handleMenuItemClick, menuItem?.correlationId]
    );

    const handleImageClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onClick?.(e, menuItem);
      },
      [onClick, menuItem]
    );

    const imageSrc = useMemo(() => {
      return menuItem?.bucketKeyName
        ? menuItem?.bucketKeyName?.includes("blob")
          ? menuItem?.bucketKeyName
          : S3_BASE_URL + menuItem?.bucketKeyName
        : RestuarantImg;
    }, [menuItem?.bucketKeyName]);

    const menuItemOptionsToShow = useMemo(() => {
      return menuItem?.itemOptions?.slice(0, 2) || [];
    }, [menuItem?.itemOptions]);

    const remainingOptionsCount = useMemo(() => {
      return Math.max(0, (menuItem?.itemOptions?.length || 0) - 3);
    }, [menuItem?.itemOptions?.length]);

    const localeLabelMap = useMemo(
      () =>
        Object.fromEntries(
          enabledLocaleOptions.map((locale: PlatformLocale) => [locale.code, locale.nativeLabel])
        ),
      [enabledLocaleOptions]
    );

    const titleDisplay = useMemo(() => {
      return resolveLocalizedDisplayName(
        menuItem?.translations,
        enabledLocales,
        defaultLocale,
        {
          englishName: menuItem?.englishName,
          nonEnglishName: menuItem?.nonEnglishName,
        }
      );
    }, [
      menuItem?.translations,
      menuItem?.englishName,
      menuItem?.nonEnglishName,
      enabledLocales,
      defaultLocale,
    ]);

    const titleTooltip = useMemo(() => {
      return buildLocalizedNameTooltip(
        menuItem?.translations,
        enabledLocales,
        {
          englishName: menuItem?.englishName,
          nonEnglishName: menuItem?.nonEnglishName,
        },
        localeLabelMap
      );
    }, [
      menuItem?.translations,
      menuItem?.englishName,
      menuItem?.nonEnglishName,
      enabledLocales,
      localeLabelMap,
    ]);

    const typeLabel = useMemo(() => {
      if (menuItemOptionsToShow.length === 0 && remainingOptionsCount === 0) return null;
      const names = menuItemOptionsToShow.map((o: MenuItemOption) => o?.name).filter(Boolean);
      const label = names.join(", ");
      return remainingOptionsCount > 0 ? `${label}${label ? ", " : ""}+${remainingOptionsCount} more` : label;
    }, [menuItemOptionsToShow, remainingOptionsCount]);

    const isInactive = menuItem.active === false;

    return (
      <div
        role="button"
        tabIndex={0}
        className="flex items-center justify-center cursor-pointer"
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardClick(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
      >
        <div
          className={`
            xl:w-[240px] w-[220px] relative ml-0 mb-0 flex flex-col group
            border border-black/[0.05] bg-[#FFFFFF] rounded-xl
            h-[180px]
            shadow-[0_4px_12px_rgba(0,0,0,0.04)]
            transition-all duration-[200ms] ease-out cursor-pointer
            hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-black/[0.08] hover:-translate-y-[2px]
            active:scale-[0.99]
            ${isSelected ? "border-[#FD7657] ring-1 ring-[#FD7657]" : ""}
            ${isInactive ? "opacity-[0.88]" : ""}
          `}
        >
          {isCombo && isSelected && (
            <img
              className="w-[18px] h-[18px] absolute right-2 top-2 z-10"
              src={CheckBox}
              alt=""
            />
          )}

          <div
            className="flex justify-center bg-[#EEF0F2] h-[50px] rounded-t-lg overflow-hidden shrink-0"
            onClick={handleImageClick}
          >
            <img
              className={`w-full h-full object-cover transition-[filter] duration-200 group-hover:brightness-[1.02] ${isInactive ? "grayscale-[0.35]" : ""}`}
              src={imageSrc}
              alt=""
            />
          </div>

          <div className="px-3 pt-2.5 pb-2.5 flex flex-col flex-1 min-h-0">
            <div
              className="line-clamp-2 text-sm font-medium text-[#374151] break-words min-h-[2.5rem] leading-[1.35]"
              style={{ fontWeight: 500 }}
              title={titleTooltip || (titleDisplay || undefined)}
            >
              {titleDisplay || "—"}
            </div>
            <div className="min-h-[22px] mt-1.5 flex items-center gap-1.5 overflow-hidden">
              {isInactive ? (
                <span className="inline-flex items-center w-fit text-[11px] text-[#92400E] font-medium rounded-full border border-[#FCD34D] bg-[#FFFBEB] py-0.5 px-2">
                  Inactive
                </span>
              ) : null}
              <TranslationCompletenessBadge
                translations={menuItem?.translations}
                enabledLocales={enabledLocales}
              />
              {typeLabel ? (
                <span
                  className="inline-flex min-w-0 items-center w-fit max-w-full text-[12px] text-[#6B7280] font-normal rounded-full border border-[#E5E7EB] bg-transparent py-1 px-2.5 truncate"
                  title={typeLabel}
                >
                  {typeLabel}
                </span>
              ) : null}
            </div>
            <div className="mt-auto pt-2.5 flex flex-row items-center justify-between gap-2 shrink-0 border-t border-black/[0.04]">
              <span className="text-base tabular-nums font-bold text-[#111]" style={{ fontWeight: 700 }}>
                {formatCentsToDollarsWithDollarSign(menuItem?.priceCents ?? 0)}
              </span>
              {!isCombo ? (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex h-6 items-center rounded-md px-2 text-[12px] font-semibold leading-none text-[#FD7657] transition-colors hover:bg-[#FFF0EB] hover:text-[#E85C32] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
                    onClick={handleEditClick}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[#9CA3AF] transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                    aria-label={`Delete ${menuItem?.englishName || "item"}`}
                    title="Delete item"
                    onClick={handleDeleteClick}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166M19.228 5.79L18.16 19.673A2.25 2.25 0 0115.916 21.75H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .563c.34-.059.68-.114 1.022-.166m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

MenuItemCard.displayName = "MenuItemCard";

export default MenuItemCard;
